import React, { useEffect, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Crown, History, Zap, Eye, Settings2, Ban } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { RoleSectionsCard } from "@/components/admin/RoleSectionsCard"; // lot 758 — « Sections visibles par rôle » vit ici (ex-Paramètres › Privilèges)

const ROLE_LABELS = {
  manager: "Gestionnaire",
  moderator: "Modérateur",
  associate: "Compte associé",
  support: "Support",
};
const ROLE_DESC = {
  manager: "Bras droit : opérations, finances, support.",
  moderator: "Veille sur le contenu et les signalements.",
  associate: "Accès en lecture seule (supervision, statistiques).",
  support: "Accès limité : vue d'ensemble et tickets support uniquement.",
};

const PRESET_LABELS = {
  all: "Tout accorder",
  none: "Tout retirer",
  support: "Support seul",
  finance: "Finances seul",
  moderation: "Modération seule",
  community: "Communauté",
};
const PRESET_ORDER = ["support", "finance", "moderation", "community", "all", "none"];

const LEVEL_LABEL = { view: "Voir", manage: "Gérer", none: "Désactivé" };

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

// Compare two {cap: level} maps and return a list of human changes.
const diffMaps = (before = {}, after = {}) => {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const out = [];
  keys.forEach((cap) => {
    const b = (before || {})[cap];
    const a = (after || {})[cap];
    if (b === a) return;
    if (!b && a) out.push({ cap, kind: "add", level: a });
    else if (b && !a) out.push({ cap, kind: "remove" });
    else out.push({ cap, kind: "change", from: b, to: a });
  });
  return out;
};

// A 3-state (or 2-state for view-only caps) segmented control.
const LevelControl = ({ level, isViewOnly, disabled, onChange }) => {
  const opts = isViewOnly
    ? [{ v: "none", label: "Désactivé", Icon: Ban }, { v: "view", label: "Voir", Icon: Eye }]
    : [{ v: "none", label: "Désactivé", Icon: Ban }, { v: "view", label: "Voir", Icon: Eye }, { v: "manage", label: "Gérer", Icon: Settings2 }];
  const colorFor = (v) => (v === "manage" ? "var(--yalola-primary)" : v === "view" ? "var(--yalola-secondary)" : "var(--yalola-text-muted)");
  return (
    <div className="inline-flex rounded-full border overflow-hidden shrink-0" style={{ borderColor: "var(--yalola-border)" }}>
      {opts.map(({ v, label, Icon }) => {
        const on = (level || "none") === v;
        return (
          <button
            key={v}
            type="button"
            disabled={disabled}
            onClick={() => onChange(v)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold transition-colors"
            style={{
              background: on ? colorFor(v) : "transparent",
              color: on ? "#fff" : "var(--yalola-text-muted)",
              cursor: disabled ? "default" : "pointer",
            }}
            data-testid={`level-opt-${v}`}
          >
            <Icon size={12} /> {label}
          </button>
        );
      })}
    </div>
  );
};

export const RolesPanel = ({ isSuper }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [searchParams] = useSearchParams();
  const focusSections = searchParams.get("focus") === "settings-role-sections"; // retour de « Voir en tant que »

  const loadAudit = () => api.get("/admin/role-permissions/audit?limit=15").then(({ data }) => setLogs(data.logs || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/role-permissions")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.friendlyMessage || "Erreur de chargement"))
      .finally(() => setLoading(false));
    loadAudit();
  }, []);

  const persist = async (role, nextMap) => {
    const prev = data;
    setData((d) => ({ ...d, roles: { ...d.roles, [role]: nextMap } }));
    setSaving(true);
    try {
      const { data: res } = await api.patch("/admin/role-permissions", { roles: { [role]: nextMap } });
      setData((d) => ({ ...d, roles: res.roles }));
      loadAudit();
    } catch (e) {
      setData(prev);
      toast.error(e?.friendlyMessage || "Impossible d'enregistrer");
    } finally { setSaving(false); }
  };

  const setLevel = (role, cap, level) => {
    if (!isSuper) return;
    const current = { ...(data.roles[role] || {}) };
    if (level === "none") delete current[cap];
    else current[cap] = level;
    persist(role, current);
  };

  const applyPreset = (role, presetKey) => {
    if (!isSuper) return;
    persist(role, { ...((data.presets || {})[presetKey] || {}) });
  };

  if (loading) {
    return <AdminSkeleton />;
  }
  if (!data) return null;

  const caps = data.capabilities || [];
  const labels = data.labels || {};
  const capKinds = data.cap_kinds || {};
  const roles = data.configurable_roles || [];
  const presets = data.presets || {};
  const presetKeys = PRESET_ORDER.filter((k) => presets[k]);

  return (
    <div className="space-y-4" data-testid="roles-panel">
      <div className="yalola-card p-4">
        <div className="font-semibold text-sm flex items-center gap-1.5"><ShieldCheck size={16} style={{ color: "var(--yalola-primary)" }} /> Gestion des rôles</div>
        <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
          Pour chaque rôle, définissez le niveau d'accès de chaque pouvoir : <b>Désactivé</b> (aucun accès), <b style={{ color: "var(--yalola-secondary)" }}>Voir</b> (consultation seule) ou <b style={{ color: "var(--yalola-primary)" }}>Gérer</b> (consulter + modifier).
          {saving && <span className="ml-2 inline-flex items-center gap-1"><Loader2 className="animate-spin" size={11} /> enregistrement…</span>}
        </p>
        <div className="mt-3 flex items-center gap-3 rounded-lg p-2.5 text-[11px] flex-wrap" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="inline-flex items-center gap-1"><Ban size={12} style={{ color: "var(--yalola-text-muted)" }} /> Désactivé</span>
          <span className="inline-flex items-center gap-1"><Eye size={12} style={{ color: "var(--yalola-secondary)" }} /> Voir (lecture)</span>
          <span className="inline-flex items-center gap-1"><Settings2 size={12} style={{ color: "var(--yalola-primary)" }} /> Gérer (action)</span>
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-lg p-2.5 text-xs" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
          <Crown size={14} /> Le <b>Super Admin</b> conserve toujours tous les pouvoirs (non modifiable).
        </div>
      </div>

      {isSuper && <RoleSectionsCard focus={focusSections} onSaved={(res) => { if (res?.roles) setData((d) => ({ ...d, roles: res.roles })); loadAudit(); }} />}

      {roles.map((role) => (
        <div key={role} className="yalola-card p-4" data-testid={`role-card-${role}`}>
          <div className="mb-3">
            <div className="font-bold text-sm">{ROLE_LABELS[role] || role}</div>
            <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{ROLE_DESC[role] || ""}</p>
          </div>

          {isSuper && presetKeys.length > 0 && (
            <div className="mb-3 flex items-center gap-1.5 flex-wrap" data-testid={`role-presets-${role}`}>
              <span className="text-[10px] font-bold uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><Zap size={11} /> Préréglages</span>
              {presetKeys.map((pk) => (
                <button key={pk} onClick={() => applyPreset(role, pk)} disabled={saving}
                  className="pill pill-outline text-[11px] py-1" data-testid={`role-preset-${role}-${pk}`}>
                  {PRESET_LABELS[pk] || pk}
                </button>
              ))}
            </div>
          )}

          <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
            {caps.map((cap) => {
              const level = (data.roles[role] || {})[cap] || "none";
              const isViewOnly = capKinds[cap] === "view";
              // Support is a tickets-only console: the overview is never granted to it.
              const locked = role === "support" && cap === "dashboard";
              return (
                <div key={cap} className="flex items-center justify-between gap-3 py-2.5" data-testid={`role-perm-${role}-${cap}`}>
                  <div className="min-w-0">
                    <span className="text-sm">{labels[cap] || cap}</span>
                    {isViewOnly && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>lecture seule</span>}
                    {locked && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid="role-perm-support-dashboard-locked">non disponible pour Support</span>}
                  </div>
                  <LevelControl
                    level={locked ? "none" : level}
                    isViewOnly={isViewOnly}
                    disabled={!isSuper || saving || locked}
                    onChange={(v) => setLevel(role, cap, v)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Audit log — who changed what & when */}
      <div className="yalola-card p-4" data-testid="roles-audit">
        <div className="font-semibold text-sm flex items-center gap-1.5"><History size={16} style={{ color: "var(--yalola-primary)" }} /> Journal des modifications de rôles</div>
        {logs.length === 0 ? (
          <p className="text-xs mt-2" style={{ color: "var(--yalola-text-muted)" }}>Aucune modification enregistrée pour le moment.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {logs.map((l, i) => {
              const meta = l.meta || {};
              const rolesChanged = meta.roles || Object.keys(meta.after || {});
              return (
                <div key={l.id || i} className="rounded-xl p-3 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`role-audit-item-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-semibold" style={{ color: "var(--yalola-text)" }}>
                      {(l.actor && l.actor.name) || meta.actor_name || "Admin"}{meta.via === "role_sections" ? <span className="ml-1 font-normal" style={{ color: "var(--yalola-text-muted)" }}>· via « Sections visibles par rôle »</span> : null}
                    </span>
                    <span style={{ color: "var(--yalola-text-muted)" }}>{fmtDate(l.created_at)}</span>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {rolesChanged.map((r) => {
                      const changes = diffMaps((meta.before || {})[r], (meta.after || {})[r]);
                      return (
                        <div key={r} style={{ color: "var(--yalola-text-muted)" }}>
                          <b style={{ color: "var(--yalola-text)" }}>{ROLE_LABELS[r] || r}</b>
                          {changes.length === 0 && <span> (aucun changement)</span>}
                          {changes.map((c, j) => (
                            <span key={j}>
                              {c.kind === "add" && <span style={{ color: "#1A7B5C" }}> +{c.cap} ({LEVEL_LABEL[c.level]})</span>}
                              {c.kind === "remove" && <span style={{ color: "#D97757" }}> −{c.cap}</span>}
                              {c.kind === "change" && <span style={{ color: "var(--yalola-secondary)" }}> {c.cap} : {LEVEL_LABEL[c.from]}→{LEVEL_LABEL[c.to]}</span>}
                            </span>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!isSuper && (
        <p className="text-xs text-center" style={{ color: "var(--yalola-text-muted)" }}>Seul le Super Admin peut modifier les permissions.</p>
      )}
    </div>
  );
};

export default RolesPanel;
